import { useEffect, useState } from "react";
import {useAuth, userHasRole} from "../../../../setup/auth/authService";
import {useTranslation} from 'react-i18next';
import {NavLink, useLocation} from 'react-router-dom';
import { AuditLogs_Viewer, GatewayLogs_Viewer, AdminWeb, HelpDeskOperator, ManageClients, ManageServices, Claims_Viewer, 
  Manage_TemplateClaims, ManageInstitutions, ManageRoles } from "../../../constants/constants";


export interface SidebarProps {
  isSidebarOpen: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isSidebarOpen }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const isInstitutionsActive = location.pathname.startsWith('/institutions');
  const isServicesActive = location.pathname.startsWith('/services');
  const isClientsActive = location.pathname.startsWith('/clients');
  const isClaimsActive = location.pathname.startsWith('/claims');
  const isAuditLogsActive = location.pathname.startsWith('/audit-logs');
  const isGatewayLogsActive = location.pathname.startsWith('/gateway-logs');
  const isConsumptionLogsActive = location.pathname.startsWith('/consumption-logs');
  const isTransactionExplorerActive = location.pathname.startsWith('/transaction-explorer');
  const isTemplateClaimsActive = location.pathname.startsWith('/templateclaims');
  const isFindProfilesActive = location.pathname.startsWith('/find-profiles') || location.pathname.startsWith('/profiles/events') || location.pathname.startsWith('/profile-details');
  const isManageAccountsActive = location.pathname.startsWith('/manage-accounts');
  const isProfileEventsHistoryActive = location.pathname.startsWith('/profile-events-history');
  const isStatisticsActive = location.pathname.startsWith('/statistics');

  const [isAuditLogViewer, setIsAuditLogViewer] = useState(false);
  const [isGatewayLogViewer, setIsGatewayLogViewer] = useState(false);
  const [isAdminWeb, setIsAdminWeb] = useState(false);
  const [isHelpDeskOperator, setIsHelpDeskOperator] = useState(false);
  const [isManageClients, setIsManageClients] = useState(false);
  const [isManageServices, setIsManageServices] = useState(false);
  const [isClaimsViewer, setIsCLaimsViewer] = useState(false);
  const [isManageTemplateCLaims, setIsManageTemplateClaims] = useState(false);
  const [isManageInstitutions, setIsManageInstitution] = useState(false);
  const [isManageRoles, setIsManageRoles] = useState(false);
   useEffect(() => {
    const checkUserRole = async () => {
      const hasAuditRole = await userHasRole(AuditLogs_Viewer);
      const hasGatewayRole = await userHasRole(GatewayLogs_Viewer);
      const hasAdminWebRole = await userHasRole(AdminWeb);
      const hasHelpDeskOperatorRole = await userHasRole(HelpDeskOperator);
      const hasManageClientsRole = await userHasRole(ManageClients);
      const hasManageServicesRole = await userHasRole(ManageServices);
      const hasClaimsRole = await userHasRole(Claims_Viewer);
      const hasTemplateClaimsRole = await userHasRole(Manage_TemplateClaims);
      const hasManageInstitutionsRole = await userHasRole(ManageInstitutions);
      const hasManageRolesRole = await userHasRole(ManageRoles);
      setIsAuditLogViewer(hasAuditRole);
      setIsGatewayLogViewer(hasGatewayRole);
      setIsAdminWeb(hasAdminWebRole);
      setIsHelpDeskOperator(hasHelpDeskOperatorRole);
      setIsManageClients(hasManageClientsRole);
      setIsManageServices(hasManageServicesRole);
      setIsCLaimsViewer(hasClaimsRole);
      setIsManageTemplateClaims(hasTemplateClaimsRole);
      setIsManageInstitution(hasManageInstitutionsRole);
      setIsManageRoles(hasManageRolesRole);
    };

    if (user && !user.expired) {
      checkUserRole();
    }
  }, [user]);
  
  return (
    <aside className={`gg-sidebar ${isSidebarOpen ? "open" : ""}`}>
      <ul className="nav flex-column p-3">
        {(!user || user.expired) ? (
          <p className="text-muted">{t('PleaseLoginToMenu')}</p>
        ) : (
          <>
            <li className="nav-item">
              <NavLink to="/" className="nav-link">
                {t('Home')}
              </NavLink>
            </li>
            {isAdminWeb && (
              <>
                {isManageClients && (
                  <li className="nav-item">
                    <NavLink to="/clients/list"  className={({ isActive }) =>
                      isActive || isClientsActive ? 'nav-link active' : 'nav-link'
                    }>
                      {t('Clients')}
                    </NavLink>
                  </li>
                )}
                {isManageInstitutions && (  
                  <li className="nav-item">
                    <NavLink to="/institutions/list"  className={({ isActive }) =>
                      isActive || isInstitutionsActive ? 'nav-link active' : 'nav-link'
                    }>
                      {t('Institutions')}
                    </NavLink>
                  </li>
                )}

                {isManageServices && (
                  <li className="nav-item">
                    <NavLink to="/services/list"  className={({ isActive }) =>
                      isActive || isServicesActive ? 'nav-link active' : 'nav-link'
                    }>
                      {t('Services')}
                    </NavLink>
                  </li>
                )}  
               
                {isClaimsViewer && (
                  <li className="nav-item">
                    <NavLink to="/claims/list"  className={({ isActive }) =>
                      isActive || isClaimsActive ? 'nav-link active' : 'nav-link'
                    }>
                      {t('Claims')}
                    </NavLink>
                  </li>
                )}

                {isManageTemplateCLaims &&(
                  <li className="nav-item">
                    <NavLink to="/templateclaims/list"  className={({ isActive }) =>
                      isActive || isTemplateClaimsActive ? 'nav-link active' : 'nav-link'
                    }>
                      {t('PredefinedTemplateClaims')}
                    </NavLink>
                  </li>
                )}

                {isAuditLogViewer && (
                  <li className="nav-item">
                    <NavLink to="/audit-logs/list"  className={({ isActive }) =>
                      isActive || isAuditLogsActive ? 'nav-link active' : 'nav-link'
                    }>
                      {t('AuditLogs')}
                    </NavLink>
                  </li>
                )}
                
                {isGatewayLogViewer && (
                  <li className="nav-item">
                    <NavLink to="/gateway-logs/list"  className={({ isActive }) =>
                      isActive || isGatewayLogsActive ? 'nav-link active' : 'nav-link'
                    }>
                      {t('GatewayLogs')}
                    </NavLink>
                  </li>
                )}

                {isGatewayLogViewer && (
                  <li className="nav-item">
                    <NavLink to="/transaction-explorer"  className={({ isActive }) =>
                      isActive || isTransactionExplorerActive ? 'nav-link active' : 'nav-link'
                    }>
                      {t('TransactionExplorer')}
                    </NavLink>
                  </li>
                )}

                {isAuditLogViewer && (
                  <>
                    <li className="nav-item">
                      <NavLink to="/consumption-logs/list" className={({ isActive }) =>
                        isActive || (isConsumptionLogsActive && !location.pathname.startsWith('/consumption-logs/reports')) ? 'nav-link active' : 'nav-link'
                      }>
                        {t('ConsumptionLogs')}
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/consumption-logs/reports" className={({ isActive }) =>
                        isActive ? 'nav-link active' : 'nav-link'
                      }>
                        {t('ConsumptionReports')}
                      </NavLink>
                    </li>
                  </>
                )}

                {isHelpDeskOperator && (
                    <li className="nav-item">
                        <NavLink to="/find-profiles"  className={({ isActive }) =>
                          isActive || isFindProfilesActive ? 'nav-link active' : 'nav-link'
                        }>
                          {t('UserProfilesList')}
                        </NavLink>
                      </li>
                )}

                 {isManageRoles && (
                      <li className="nav-item">
                      <NavLink to="/manage-accounts"  className={({ isActive }) =>
                        isActive || isManageAccountsActive ? 'nav-link active' : 'nav-link'
                      }>
                        {t('ManageAccounts')}
                      </NavLink>
                    </li>
                )}

                {isHelpDeskOperator && (
                    <li className="nav-item">
                        <NavLink to="/profile-events-history" className={({ isActive }) =>
                            isActive || isProfileEventsHistoryActive ? 'nav-link active' : 'nav-link'
                        }>
                            {t('ProfileEventsHistory')}
                        </NavLink>
                    </li>
                )}

                {isHelpDeskOperator && (
                    <li className="nav-item">
                        <NavLink to="/statistics" className={({ isActive }) =>
                            isActive || isStatisticsActive ? 'nav-link active' : 'nav-link'
                        }>
                            {t('Statistics')}
                        </NavLink>
                    </li>
                )}
              </>
            )}
            
          </>
        )}
      </ul>
    </aside>
  );
};

export default Sidebar;
